import { useState, useEffect, useRef, useContext } from "react";
import { ImageContext } from "../Component/Context/ImageContext";
import { applyPerspectiveWarp } from "../utils/newPerspectiveWarp";
import { DEFAULT_POINTS, CANVAS_DIMENSIONS } from "../Constants/constants";
import { generateGliderolFilename } from "../utils/fileNaming";

// Set your desired export width here
const EXPORT_WIDTH = 1200;

export const useImageHandling = (points, image, backImage, setBackImage, setPoints) => {
  const { setImgurl } = useContext(ImageContext);
  const [bgKonvaImage, setBgKonvaImage] = useState(null);
  const [houseImageDimensions, setHouseImageDimensions] = useState({
    width: CANVAS_DIMENSIONS.WIDTH,
    height: CANVAS_DIMENSIONS.HEIGHT,
  });

  useEffect(() => {
    if (backImage) {
      setBgKonvaImage(backImage);
      setTimeout(() => {
        handleGenerateImage();
      }, 200);
    }
  }, [backImage]);

  useEffect(() => {
    if (!bgKonvaImage || !image || points.length !== 4) return;

    const timeout = setTimeout(() => {
      handleGenerateImage();
    }, 100);

    return () => clearTimeout(timeout);
  }, [bgKonvaImage, image, points]);

  const selectgalleryimage = (imgUrl) => {
    const img = new window.Image();
    img.crossOrigin = "Anonymous";
    img.referrerPolicy = "no-referrer";
    img.src = imgUrl;

    img.onload = () => {
      setBackImage(img.src);
      
      const pointsMap = {
        "Platinum": DEFAULT_POINTS.PLATINUM,
        "Roller Door": DEFAULT_POINTS.ROLLER_DOOR,
        "section_hampton_windspray": DEFAULT_POINTS.SECTION_HAMPTON_WINDSPRAY,
        "Sectional Door": DEFAULT_POINTS.SECTIONAL_DOOR,
        "sectional_madison_monument_smooth_home": DEFAULT_POINTS.SECTIONAL_MADISON_MONUMENT,
        "sectional_madison_nightsky": DEFAULT_POINTS.SECTIONAL_MADISON_NIGHTSKY,
        "sectional_tuscan_dover_white": DEFAULT_POINTS.SECTIONAL_TUSCAN_DOVER,
        "Selections": DEFAULT_POINTS.SELECTIONS,
      };

      const matchedKey = Object.keys(pointsMap).find(key => imgUrl.includes(key));
      setPoints(matchedKey ? pointsMap[matchedKey] : DEFAULT_POINTS.DEFAULT);
    };

    img.onerror = (e) => {
      console.error("Image failed to load", e);
    };
  };

  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();

      reader.onload = (event) => {
        const img = new window.Image();
        img.onload = () => {
          setBackImage(event.target.result);
        };
        img.src = event.target.result;
      };

      reader.readAsDataURL(file);
    }
  };

  // High-resolution save function
  const handleSaveImage = async () => {
    if (!bgKonvaImage || !image || points.length !== 4) return;
    
    console.log('=== DEBUG SAVE IMAGE ===');
    console.log('bgKonvaImage:', bgKonvaImage);
    console.log('image:', image);
    console.log('points:', points);

    // Set your desired export width here
    const EXPORT_WIDTH = 1200;
    
    // Calculate scale factor
    const originalWidth = CANVAS_DIMENSIONS.WIDTH; // 840
    const scaleFactor = EXPORT_WIDTH / originalWidth;
    const exportHeight = Math.round(CANVAS_DIMENSIONS.HEIGHT * scaleFactor);
    
    console.log('scaleFactor:', scaleFactor);
    console.log('scaledPoints:', points.map(point => ({
      x: point.x * scaleFactor,
      y: point.y * scaleFactor
    })));

    // Create high-resolution canvas
    const canvas = document.createElement("canvas");
    canvas.width = EXPORT_WIDTH;
    canvas.height = exportHeight;
    const ctx = canvas.getContext("2d");
    ctx.filter = 'blur(0.5px)'; // Very subtle blur for anti-aliasing

    // Enable high-quality rendering
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = 'high';

    const bg = new Image();
    bg.crossOrigin = "anonymous";
    bg.src = bgKonvaImage;

    const door = new Image();
    door.crossOrigin = "anonymous";
    door.src = image;

    await Promise.all([
      new Promise((res) => (bg.onload = res)),
      new Promise((res) => (door.onload = res)),
    ]);

    // Draw background at high resolution
    ctx.drawImage(bg, 0, 0, EXPORT_WIDTH, exportHeight);

    // Scale points for high resolution
    const scaledPoints = points.map(point => ({
      x: point.x * scaleFactor,
      y: point.y * scaleFactor
    }));

    console.log('=== BEFORE PERSPECTIVE WARP ===');
    console.log('Canvas size:', canvas.width, 'x', canvas.height);
    console.log('Door image natural size:', door.naturalWidth, 'x', door.naturalHeight);
    console.log('From points (door corners):', [
      { x: 0, y: 0 },
      { x: door.naturalWidth, y: 0 },
      { x: door.naturalWidth, y: door.naturalHeight },
      { x: 0, y: door.naturalHeight },
    ]);
    console.log('To points (scaled):', scaledPoints);

    // Use your existing applyPerspectiveWarp function but with scaled points
    applyPerspectiveWarp({
      ctx,
      img: door,
      from: [
        { x: 0, y: 0 },
        { x: door.naturalWidth, y: 0 },
        { x: door.naturalWidth, y: door.naturalHeight },
        { x: 0, y: door.naturalHeight },
      ],
      to: scaledPoints,
    });

    console.log('=== AFTER PERSPECTIVE WARP ===');

    ctx.filter = 'none'; // Reset filter

    // Download high-quality image
    const link = document.createElement("a");
    link.download = generateGliderolFilename();
    link.href = canvas.toDataURL("image/jpeg", 0.92);
    link.click();

    console.log(`Exported at ${EXPORT_WIDTH}x${exportHeight}px`);
  };

  // Keep the preview generation at normal resolution for performance
  const handleGenerateImage = async () => {
    if (!bgKonvaImage || !image || points.length !== 4) return;

    const canvas = document.createElement("canvas");
    canvas.width = CANVAS_DIMENSIONS.WIDTH;
    canvas.height = CANVAS_DIMENSIONS.HEIGHT;
    const ctx = canvas.getContext("2d");

    const bg = new Image();
    bg.crossOrigin = "anonymous";
    bg.src = bgKonvaImage;

    const door = new Image();
    door.crossOrigin = "anonymous";
    door.src = image;

    await Promise.all([
      new Promise((res) => (bg.onload = res)),
      new Promise((res) => (door.onload = res)),
    ]);

    ctx.drawImage(bg, 0, 0, canvas.width, canvas.height);

    // Use regular quality for live preview (faster)
    applyPerspectiveWarp({
      ctx,
      img: door,
      from: [
        { x: 0, y: 0 },
        { x: door.naturalWidth, y: 0 },
        { x: door.naturalWidth, y: door.naturalHeight },
        { x: 0, y: door.naturalHeight },
      ],
      to: points,
    });
    
    canvas.toBlob((blob) => {
      if (!blob) return;
      const url = URL.createObjectURL(blob);
      setImgurl(url);
    }, "image/png");
  };

  return {
    bgKonvaImage,
    houseImageDimensions,
    setHouseImageDimensions,
    selectgalleryimage,
    handleImageUpload,
    handleSaveImage,
    handleGenerateImage,
  };
};

/**
 * FORCE 1200px version - bypasses all scaling calculations
 * This version detects the actual container size and scales appropriately
 */
export const generateHighResImageForce1200 = async ({
  bgImage,
  doorImage,
  points,
  houseImageDimensions,
  containerDimensions = { width: 370, height: 300 },
  format = 'jpeg',
  quality = 0.95
}) => {
  if (!bgImage || !doorImage || !points || points.length !== 4) {
    throw new Error('Missing required parameters for image generation');
  }

  console.log('🚀 FORCE 1200PX VERSION - BYPASSING ALL CALCULATIONS');
  console.log('Original points from dragging:', points);
  console.log('Container dimensions passed:', containerDimensions);

  // FORCE 1200px width, calculate height maintaining aspect ratio
  const FORCED_WIDTH = 1200;
  
  // Load background image to get its natural aspect ratio
  const bgImg = new Image();
  bgImg.crossOrigin = "anonymous";
  bgImg.src = bgImage;
  
  await new Promise((resolve, reject) => {
    bgImg.onload = resolve;
    bgImg.onerror = reject;
  });

  const aspectRatio = bgImg.naturalHeight / bgImg.naturalWidth;
  const FORCED_HEIGHT = Math.round(FORCED_WIDTH * aspectRatio);
  
  console.log('🚀 FORCED dimensions:', FORCED_WIDTH, 'x', FORCED_HEIGHT);
  console.log('🚀 Background image natural size:', bgImg.naturalWidth, 'x', bgImg.naturalHeight);

  // Create canvas with FORCED dimensions
  const canvas = document.createElement("canvas");
  canvas.width = FORCED_WIDTH;
  canvas.height = FORCED_HEIGHT;
  
  console.log('🚀 Canvas created with FORCED size:', canvas.width, 'x', canvas.height);
  
  const ctx = canvas.getContext("2d");
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = 'high';

  // Load door image
  const door = new Image();
  door.crossOrigin = "anonymous";
  door.src = doorImage;
  
  await new Promise((resolve, reject) => {
    door.onload = resolve;
    door.onerror = reject;
  });

  // Draw background at FORCED size
  ctx.drawImage(bgImg, 0, 0, FORCED_WIDTH, FORCED_HEIGHT);
  console.log('🚀 Background drawn at FORCED size');

  // CRITICAL FIX: Detect if we're coming from desktop (840px) or mobile (370px)
  let actualContainerWidth = containerDimensions.width;
  
  // If container width is around 840, it's probably desktop using CANVAS_DIMENSIONS
  // If it's around 370, it's mobile
  // Scale points accordingly
  
  console.log('🚀 Detected container width:', actualContainerWidth);
  
  const scaleX = FORCED_WIDTH / actualContainerWidth;
  const scaleY = FORCED_HEIGHT / containerDimensions.height;
  
  console.log('🚀 Scale factors - X:', scaleX, 'Y:', scaleY);
  console.log('🚀 This means scaling from', actualContainerWidth, 'to', FORCED_WIDTH);
  
  const scaledPoints = points.map(point => ({
    x: point.x * scaleX,
    y: point.y * scaleY
  }));
  
  console.log('🚀 Original points:', points);
  console.log('🚀 Scaled points:', scaledPoints);
  console.log('🚀 Canvas before perspective warp:', canvas.width, 'x', canvas.height);

  // Apply perspective warp
  applyPerspectiveWarp({
    ctx,
    img: door,
    from: [
      { x: 0, y: 0 },
      { x: door.naturalWidth, y: 0 },
      { x: door.naturalWidth, y: door.naturalHeight },
      { x: 0, y: door.naturalHeight },
    ],
    to: scaledPoints,
  });

  console.log('🚀 Canvas after perspective warp:', canvas.width, 'x', canvas.height);

  // Convert to data URL
  const mimeType = format === 'png' ? 'image/png' : 'image/jpeg';
  const dataUrl = canvas.toDataURL(mimeType, quality);

  // Final verification
  const testImg = new Image();
  testImg.src = dataUrl;
  await new Promise(resolve => {
    testImg.onload = () => {
      console.log('🚀 FINAL FORCE 1200 VERIFICATION:', testImg.naturalWidth, 'x', testImg.naturalHeight);
      if (testImg.naturalWidth !== 1200) {
        console.error('🚨 FORCE 1200 FAILED! Actual width:', testImg.naturalWidth);
      } else {
        console.log('✅ FORCE 1200 SUCCESS!');
      }
      resolve();
    };
  });
  
  return dataUrl;
};